import {useEffect, useState} from 'react'
import {useI18n} from '@/i18n'
import {Button} from '@/components/ui/button'
import {ButtonSpinner} from '@/components/shared/ButtonSpinner'
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import {DialogBody} from '@/components/shared/DialogBody'
import {Input} from '@/components/ui/input'
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from '@/components/ui/select'
import {SecretReferenceInput} from '@/components/shared/SecretReferenceInput'
import {FormField} from '@/components/shared/FormField'
import {FormGrid} from '@/components/shared/FormGrid'
import {FormSection} from '@/components/shared/FormSection'
import type {SapHcmInboundConfig} from '@/types/inbound-connector'
import {useUpdateSapHcmConfig} from '@/hooks/use-sap-configs'

interface SapHcmInboundConfigEditDialogProps {
    connectorId: string
    config: SapHcmInboundConfig
    open: boolean
    onClose: () => void
}

const VALIDITY_OPTIONS = ['CURRENT', 'LATEST', 'ALL_BANDS'] as const
const AUTH_TYPE_OPTIONS = ['BASIC_AUTH', 'OAUTH2_CLIENT', 'BEARER_TOKEN'] as const

export function SapHcmInboundConfigEditDialog({
    connectorId, config, open, onClose,
}: SapHcmInboundConfigEditDialogProps) {
    const i18n = useI18n()
    const updateMutation = useUpdateSapHcmConfig(connectorId)

    const [baseUrl, setBaseUrl] = useState(config.baseUrl)
    const [employeeKeyField, setEmployeeKeyField] = useState(config.employeeKeyField)
    const [validityStartField, setValidityStartField] = useState(config.validityStartField)
    const [validityEndField, setValidityEndField] = useState(config.validityEndField)
    const [validityResolutionStrategy, setValidityResolutionStrategy] = useState(config.validityResolutionStrategy)
    const [authType, setAuthType] = useState(config.authType)
    const [tokenSecretRef, setTokenSecretRef] = useState('')
    const [tokenUrl, setTokenUrl] = useState(config.tokenUrl ?? '')
    const [clientId, setClientId] = useState(config.clientId ?? '')
    const [clientSecretRef, setClientSecretRef] = useState('')
    const [scope, setScope] = useState(config.scope ?? '')
    const [username, setUsername] = useState(config.username ?? '')
    const [passwordSecretRef, setPasswordSecretRef] = useState('')
    const [pageSize, setPageSize] = useState(String(config.pageSize))
    const [connectTimeoutMs, setConnectTimeoutMs] = useState(String(config.connectTimeoutMs))
    const [readTimeoutMs, setReadTimeoutMs] = useState(String(config.readTimeoutMs))
    const [maxRetries, setMaxRetries] = useState(String(config.maxRetries))
    const [terminationAttribute, setTerminationAttribute] = useState(config.terminationAttribute)
    const [terminationValuesCsv, setTerminationValuesCsv] = useState(config.terminationValuesCsv)

    useEffect(() => {
        if (open) {
            setBaseUrl(config.baseUrl)
            setEmployeeKeyField(config.employeeKeyField)
            setValidityStartField(config.validityStartField)
            setValidityEndField(config.validityEndField)
            setValidityResolutionStrategy(config.validityResolutionStrategy)
            setAuthType(config.authType)
            setTokenSecretRef('')
            setTokenUrl(config.tokenUrl ?? '')
            setClientId(config.clientId ?? '')
            setClientSecretRef('')
            setScope(config.scope ?? '')
            setUsername(config.username ?? '')
            setPasswordSecretRef('')
            setPageSize(String(config.pageSize))
            setConnectTimeoutMs(String(config.connectTimeoutMs))
            setReadTimeoutMs(String(config.readTimeoutMs))
            setMaxRetries(String(config.maxRetries))
            setTerminationAttribute(config.terminationAttribute)
            setTerminationValuesCsv(config.terminationValuesCsv)
        }
    }, [open, config])

    const handleSave = () => {
        updateMutation.mutate({
            baseUrl,
            employeeKeyField,
            validityStartField,
            validityEndField,
            validityResolutionStrategy,
            authType,
            tokenSecretRef: tokenSecretRef || null,
            tokenUrl: tokenUrl || null,
            clientId: clientId || null,
            clientSecretRef: clientSecretRef || null,
            scope: scope || null,
            username: username || null,
            passwordSecretRef: passwordSecretRef || null,
            pageSize: parseInt(pageSize, 10) || 1,
            connectTimeoutMs: parseInt(connectTimeoutMs, 10) || 5000,
            readTimeoutMs: parseInt(readTimeoutMs, 10) || 30000,
            maxRetries: parseInt(maxRetries, 10) || 0,
            terminationAttribute,
            terminationValuesCsv,
            enabledInfotypes: null,
            attributeMappings: null,
            version: config.version,
        }, {onSuccess: onClose})
    }

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-2xl overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>{i18n.inboundConnectors.sapHcm.editDialogTitle}</DialogTitle>
                    <DialogDescription>{i18n.inboundConnectors.sapHcm.editDialogDescription}</DialogDescription>
                </DialogHeader>
                <DialogBody>
                    <FormSection title={i18n.inboundConnectors.sectionConnection}>
                        <FormGrid columns={1}>
                            <FormField label={i18n.inboundConnectors.baseUrl} htmlFor="sap-hcm-baseUrl" required>
                                <Input id="sap-hcm-baseUrl" value={baseUrl} onChange={e => setBaseUrl(e.target.value)}/>
                            </FormField>
                        </FormGrid>
                    </FormSection>

                    <FormSection title={i18n.inboundConnectors.sapHcm.sectionWireFields}>
                        <FormGrid columns={1}>
                            <FormField label={i18n.inboundConnectors.sapHcm.employeeKeyField}
                                       htmlFor="sap-hcm-employeeKeyField" required>
                                <Input id="sap-hcm-employeeKeyField" value={employeeKeyField}
                                       data-testid="input-sap-hcm-employee-key-field"
                                       onChange={e => setEmployeeKeyField(e.target.value)}/>
                            </FormField>
                        </FormGrid>
                        <FormGrid columns={2}>
                            <FormField label={i18n.inboundConnectors.sapHcm.validityStartField}
                                       htmlFor="sap-hcm-validityStartField" required>
                                <Input id="sap-hcm-validityStartField" value={validityStartField}
                                       data-testid="input-sap-hcm-validity-start-field"
                                       onChange={e => setValidityStartField(e.target.value)}/>
                            </FormField>
                            <FormField label={i18n.inboundConnectors.sapHcm.validityEndField}
                                       htmlFor="sap-hcm-validityEndField" required>
                                <Input id="sap-hcm-validityEndField" value={validityEndField}
                                       data-testid="input-sap-hcm-validity-end-field"
                                       onChange={e => setValidityEndField(e.target.value)}/>
                            </FormField>
                            <FormField label={i18n.inboundConnectors.sapHcm.validityResolutionStrategy}
                                       htmlFor="sap-hcm-validity" required>
                                <Select value={validityResolutionStrategy} onValueChange={setValidityResolutionStrategy}>
                                    <SelectTrigger id="sap-hcm-validity" data-testid="sap-hcm-validity-select">
                                        <SelectValue/>
                                    </SelectTrigger>
                                    <SelectContent>
                                        {VALIDITY_OPTIONS.map(o => (
                                            <SelectItem key={o} value={o}>{o}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </FormField>
                        </FormGrid>
                    </FormSection>

                    <FormSection title={i18n.inboundConnectors.sectionAuthentication}>
                        <FormGrid columns={1}>
                            <FormField label={i18n.inboundConnectors.authType} htmlFor="sap-hcm-authType" required>
                                <Select value={authType} onValueChange={setAuthType}>
                                    <SelectTrigger id="sap-hcm-authType" data-testid="select-sap-hcm-auth-type">
                                        <SelectValue/>
                                    </SelectTrigger>
                                    <SelectContent>
                                        {AUTH_TYPE_OPTIONS.map(o => (
                                            <SelectItem key={o} value={o}>{o}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </FormField>
                        </FormGrid>
                        {authType === 'BASIC_AUTH' && (
                            <FormGrid columns={2}>
                                <FormField label={i18n.inboundConnectors.username} htmlFor="sap-hcm-username">
                                    <Input id="sap-hcm-username" value={username} onChange={e => setUsername(e.target.value)}/>
                                </FormField>
                                <FormField label={i18n.inboundConnectors.passwordRef} htmlFor="sap-hcm-passwordSecretRef">
                                    <SecretReferenceInput id="sap-hcm-passwordSecretRef"
                                                          value={passwordSecretRef}
                                                          onChange={setPasswordSecretRef}/>
                                </FormField>
                            </FormGrid>
                        )}
                        {authType === 'OAUTH2_CLIENT' && (
                            <FormGrid columns={2}>
                                <FormField label={i18n.inboundConnectors.clientId} htmlFor="sap-hcm-clientId">
                                    <Input id="sap-hcm-clientId" value={clientId} onChange={e => setClientId(e.target.value)}/>
                                </FormField>
                                <FormField label={i18n.inboundConnectors.clientSecretRef} htmlFor="sap-hcm-clientSecretRef">
                                    <SecretReferenceInput id="sap-hcm-clientSecretRef"
                                                          value={clientSecretRef}
                                                          onChange={setClientSecretRef}/>
                                </FormField>
                                <FormField label={i18n.inboundConnectors.tokenUrl} htmlFor="sap-hcm-tokenUrl">
                                    <Input id="sap-hcm-tokenUrl" value={tokenUrl} onChange={e => setTokenUrl(e.target.value)}/>
                                </FormField>
                                <FormField label={i18n.inboundConnectors.oauthScope} htmlFor="sap-hcm-scope">
                                    <Input id="sap-hcm-scope" value={scope} onChange={e => setScope(e.target.value)}/>
                                </FormField>
                            </FormGrid>
                        )}
                        {authType === 'BEARER_TOKEN' && (
                            <FormGrid columns={1}>
                                <FormField label={i18n.inboundConnectors.tokenRef} htmlFor="sap-hcm-tokenSecretRef">
                                    <SecretReferenceInput id="sap-hcm-tokenSecretRef"
                                                          value={tokenSecretRef}
                                                          onChange={setTokenSecretRef}/>
                                </FormField>
                            </FormGrid>
                        )}
                    </FormSection>

                    <FormSection title={i18n.inboundConnectors.sectionPagination}>
                        <FormGrid columns={2}>
                            <FormField label={i18n.inboundConnectors.pageSize} htmlFor="sap-hcm-pageSize">
                                <Input id="sap-hcm-pageSize" type="number" value={pageSize}
                                       onChange={e => setPageSize(e.target.value)}/>
                            </FormField>
                            <FormField label={i18n.inboundConnectors.connectTimeout} htmlFor="sap-hcm-connectTimeout">
                                <Input id="sap-hcm-connectTimeout" type="number" value={connectTimeoutMs}
                                       onChange={e => setConnectTimeoutMs(e.target.value)}/>
                            </FormField>
                            <FormField label={i18n.inboundConnectors.readTimeout} htmlFor="sap-hcm-readTimeout">
                                <Input id="sap-hcm-readTimeout" type="number" value={readTimeoutMs}
                                       onChange={e => setReadTimeoutMs(e.target.value)}/>
                            </FormField>
                            <FormField label={i18n.inboundConnectors.maxRetries} htmlFor="sap-hcm-maxRetries">
                                <Input id="sap-hcm-maxRetries" type="number" value={maxRetries}
                                       onChange={e => setMaxRetries(e.target.value)}/>
                            </FormField>
                        </FormGrid>
                    </FormSection>

                    <FormSection title={i18n.inboundConnectors.sapHcm.sectionTermination}>
                        <FormGrid columns={2}>
                            <FormField label={i18n.inboundConnectors.sapHcm.terminationAttribute} htmlFor="sap-hcm-terminationAttribute" required>
                                <Input id="sap-hcm-terminationAttribute" value={terminationAttribute}
                                       onChange={e => setTerminationAttribute(e.target.value)}/>
                            </FormField>
                            <FormField label={i18n.inboundConnectors.sapHcm.terminationValuesCsv} htmlFor="sap-hcm-terminationValuesCsv" required>
                                <Input id="sap-hcm-terminationValuesCsv" value={terminationValuesCsv}
                                       onChange={e => setTerminationValuesCsv(e.target.value)}/>
                            </FormField>
                        </FormGrid>
                    </FormSection>
                </DialogBody>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} data-testid="btn-cancel-sap-hcm-config">{i18n.common.cancel}</Button>
                    <Button onClick={handleSave} disabled={updateMutation.isPending}
                            data-testid="btn-save-sap-hcm-config">
                        {updateMutation.isPending && <ButtonSpinner/>}
                        {i18n.common.save}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
